import React from 'react';
import { Control, Controller } from 'react-hook-form';
import { FormControl, FormControlLabel, FormLabel, Radio, RadioGroup } from '@material-ui/core';
import { RadioGroupProps } from '@material-ui/core/RadioGroup';

type Option<V = string> = {
  value: V;
  label: React.ReactNode;
  disabled?: boolean;
};

export type RHFRadioGroupProps<V = string> = Omit<RadioGroupProps, 'name' | 'value'> & {
  name: string;
  control: Control<any>;
  label?: React.ReactNode;
  options: Option<V>[];
  helperText?: React.ReactNode;
  row?: boolean;
  disabled?: boolean;
  formControlProps?: React.ComponentProps<typeof FormControl>;
};

const RHFRadioGroup = React.forwardRef<HTMLDivElement, RHFRadioGroupProps<any>>(
  (
    {
      name,
      control,
      label,
      options,
      helperText,
      row = true,
      disabled,
      formControlProps,
      ...radioGroupProps
    },
    ref,
  ) => {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => {
          const isDisabled = disabled || field.disabled;
          const hasError = !!fieldState.error;
          const ariaLabelId = label ? `${name}-label` : undefined;
          const value = field.value ?? '';

          return (
            <FormControl error={hasError} disabled={isDisabled} {...formControlProps}>
              {label && <FormLabel id={ariaLabelId}>{label}</FormLabel>}

              <RadioGroup
                {...radioGroupProps}
                ref={ref}
                aria-labelledby={ariaLabelId}
                row={row}
                value={String(value)}
                onChange={(_, val) => {
                  const opt = options.find((o) => String(o.value) === val);
                  field.onChange(opt ? opt.value : val);
                }}
                onBlur={field.onBlur}
                name={field.name}
              >
                {options.map((opt) => (
                  <FormControlLabel
                    key={String(opt.value)}
                    value={String(opt.value)}
                    control={<Radio />}
                    label={opt.label}
                    disabled={opt.disabled}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          );
        }}
      />
    );
  },
);

RHFRadioGroup.displayName = 'RHFRadioGroup';
export default RHFRadioGroup;
